// Lớp gọi API.
//
// MỘT NGUYÊN TẮC: `credentials: 'include'` ở MỌI request.
// Phiên đăng nhập nằm trong cookie HttpOnly — không đọc được bằng JavaScript.
// Thiếu `credentials` thì cookie không được gửi, mọi request trả 401, và
// lỗi hiện ra là "sai mật khẩu" dù mật khẩu đúng. Dễ chẩn đoán sai.

import { ApiError } from './types'
import type {
  CoreIssue,
  Order,
  Plan,
  Progress,
  Question,
  Session,
  Topic,
  User,
  WrongQuestion,
  WrongTopicCount,
} from './types'

const BASE = '/api/v1'

/** Đường dẫn tuyệt đối, bỏ qua tiền tố `/api/v1`. Dùng cho `/healthz`. */
function callAbs<T>(path: string, init: RequestInit = {}): Promise<T> {
  return call<T>(path, init, '')
}

async function call<T>(path: string, init: RequestInit = {}, prefix = BASE): Promise<T> {
  let res: Response
  try {
    res = await fetch(prefix + path, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch {
    // fetch chỉ reject khi không tới được server (mất mạng, DNS, server chết).
    // Backend có thể vẫn còn sống; phân biệt rõ để UI không báo "sai mật khẩu".
    throw new ApiError(0, 'offline', 'Không kết nối được máy chủ.')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let body: unknown
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    throw new ApiError(res.status, 'bad_response', 'Máy chủ trả về nội dung không đọc được.')
  }

  if (!res.ok) {
    const e = body as { code?: string; detail?: string }
    throw new ApiError(
      res.status,
      e.code ?? 'http_' + res.status,
      e.detail ?? 'Không rõ nguyên nhân.',
    )
  }
  return body as T
}

function body(v: unknown): string {
  return JSON.stringify(v)
}

export const api = {
  // ---- hệ thống ----
  // Nằm ngoài /api/v1 nên gọi bằng callAbs.
  health: () =>
    callAbs<{ status: string; release_questions: number; server_now: string }>('/healthz'),

  // ---- tài khoản ----
  me: () => call<User>('/auth/me'),
  register: (email: string, password: string) =>
    call<User>('/auth/register', { method: 'POST', body: body({ email, password }) }),
  login: (email: string, password: string) =>
    call<User>('/auth/login', { method: 'POST', body: body({ email, password }) }),
  logout: () => call<void>('/auth/logout', { method: 'POST' }),
  /** Đổi mật khẩu. Server đồng thời đăng xuất MỌI máy khác — nên trả về số
   *  phiên đã đuổi để báo người dùng, im lặng thì họ tưởng máy khác vẫn an toàn. */
  changePassword: (currentPassword: string, newPassword: string) =>
    call<{ password_changed: boolean; other_sessions_gone: number }>('/auth/change-password', {
      method: 'POST',
      body: body({ current_password: currentPassword, new_password: newPassword }),
    }),

  // ---- nội dung ----
  topics: () => call<{ topics: Topic[]; has_full: boolean }>('/topics'),
  topicIssues: (slug: string) =>
    call<{ topic: string; issues: CoreIssue[] }>(`/topics/${encodeURIComponent(slug)}/issues`),
  progress: () => call<Progress>('/progress'),

  // ---- câu sai ----
  // `total` là tổng THẬT của cả danh sách, không phải số dòng trả về — server
  // đếm riêng trước khi lấy. Phân trang bằng `limit`/`offset` vì danh sách có
  // thể dài hàng trăm câu.
  wrongQuestions: (topic = '', limit = 50, offset = 0) => {
    const q = new URLSearchParams({ limit: String(limit), offset: String(offset) })
    if (topic) q.set('topic', topic)
    return call<{
      total: number
      limit: number
      offset: number
      items: WrongQuestion[]
      by_topic: WrongTopicCount[]
    }>(`/wrong-questions?${q}`)
  },

  // ---- làm bài ----
  createSession: (
    mode: 'practice' | 'timed',
    topic: string,
    count: number,
    timeLimitSec?: number,
    /** Luyện đúng các câu này (màn Câu đã sai) — backend bỏ qua `topic`/`count`
     *  khi có danh sách. */
    questionIds?: string[],
  ) =>
    call<Session>('/sessions', {
      method: 'POST',
      // time_limit_sec phải là null khi practice, không phải undefined — backend
      // phân biệt qua giá trị này. Gửi undefined thì JSON bỏ hẳn trường, mà
      // DisallowUnknownFields/validate yêu cầu `null` cho mode=practice.
      body: body({
        mode,
        topic,
        count,
        time_limit_sec: mode === 'timed' ? (timeLimitSec ?? 0) : null,
        // Luôn gửi kể cả rỗng: backend phân biệt "không truyền" với "truyền
        // mảng rỗng" bằng nil, mảng rỗng thì y như không truyền.
        question_ids: questionIds ?? null,
      }),
    }),
  getSession: (id: string) => call<Session>(`/sessions/${id}`),
  answer: (sessionId: string, sessionQuestionId: string, optionKey: string, timeSpentSec?: number) =>
    call<{ accepted: boolean }>(`/sessions/${sessionId}/answers`, {
      method: 'POST',
      body: body({
        session_question_id: sessionQuestionId,
        option_key: optionKey,
        time_spent_sec: timeSpentSec ?? null,
      }),
    }),
  submit: (id: string) => call<NonNullable<Session['result']>>(`/sessions/${id}/submit`, { method: 'POST' }),

  // ---- thanh toán ----
  plans: () => call<{ plans: Plan[] }>('/plans'),
  /** Chỉ gửi SỐ THÁNG, không gửi số tiền. Server tự chốt giá — nhận
   *  `amount_vnd` từ client thì sửa một request là mua 12 tháng với giá 1đ. */
  createOrder: (planMonths: number) =>
    call<Order>('/orders', { method: 'POST', body: body({ plan_months: planMonths }) }),
  /** Đơn chờ trả tiền mới nhất. `order` là `null` khi không có — khác hẳn với
   *  lỗi mạng, nên màn thanh toán dừng poll thay vì báo hỏng. */
  currentOrder: () => call<{ order: Order | null }>('/orders/current'),
}

export type { Question, Session, Topic, Progress, CoreIssue, User }
